import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, ArrowRight } from 'lucide-react';
import { featuredLocations } from '@/data/locations';

const serviceLinks = [
  { label: 'DLP Chip Repair', href: '/services/white-dots-dmd' },
  { label: 'Power & Overheating', href: '/services/overheating-power' },
  { label: 'Display Discolouration', href: '/services/display-discolouration' },
  { label: 'Laser & Lamp Service', href: '/services/laser-lamp' },
];

const companyLinks = [
  { label: 'About Us', href: '/about' },
  { label: 'Contact Us', href: '/contact' },
  { label: 'Warranty & Returns', href: '/warranty' },
  { label: 'Terms & Conditions', href: '/terms' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Repair Enquiry', href: '/repair-enquiry' },
];

export default function Footer() {
  return (
    <footer className="bg-[#0057B7] border-t border-white/10">
      <div className="container-custom py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#FFDD00] text-[#0057B7]">
                <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-white">
                  <rect x="2" y="6" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
                  <circle cx="14" cy="11" r="3" stroke="currentColor" strokeWidth="2" />
                  <path d="M18 9l4-2M18 13l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div className="leading-tight">
                <span className="block text-[15px] font-heading font-extrabold text-white tracking-tight">PROJECTOR REPAIR</span>
                <span className="block text-[11px] font-label text-[#FFDD00] tracking-[0.15em] -mt-0.5">LONDON</span>
              </div>
            </Link>
            <p className="mt-4 text-sm text-white/70 leading-relaxed">
              Specialist projector repair centre serving London and the UK. Expert diagnostics for DLP, LCD, laser and xenon projectors.
            </p>
            <a href="tel:+443330064326" className="mt-5 inline-flex items-center gap-2 text-[#FFDD00] hover:text-[#C9A800] transition-colors text-sm font-semibold">
              <Phone className="w-4 h-4" />
              +44 (0)333 006 4326
            </a>
          </div>

          {/* UK Repair Centre */}
          <div>
            <h3 className="font-label text-[#FFDD00] mb-4">UK Repair Centre</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-white/70">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#FFDD00]" />
                <span>Unit 9, West Moor Park<br />Yorkshire Way<br />Doncaster, DN3 3GW</span>
              </li>
              <li>
                <a href="tel:+443330064326" className="flex items-start gap-3 text-sm text-white/70 hover:text-[#0057B7] transition-colors">
                  <Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#FFDD00]" />
                  <span>0333 006 4326</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/70">
                <Clock className="w-4 h-4 mt-0.5 shrink-0 text-[#FFDD00]" />
                <span>Mon &ndash; Fri / 9:00 &ndash; 17:00</span>
              </li>
            </ul>
          </div>

          {/* Ireland Repair Centre */}
          <div>
            <h3 className="font-label text-[#FFDD00] mb-4">Ireland Repair Centre</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-white/70">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#FFDD00]" />
                <span>Unit 7b, Universal Business Park<br />Bluebell, Dublin, D12</span>
              </li>
              <li>
                <a href="tel:+35315827344" className="flex items-start gap-3 text-sm text-white/70 hover:text-[#0057B7] transition-colors">
                  <Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#FFDD00]" />
                  <span>+353 1 582 7344</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/70">
                <Clock className="w-4 h-4 mt-0.5 shrink-0 text-[#FFDD00]" />
                <span>Mon &ndash; Fri / 9:00 &ndash; 17:00</span>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="font-label text-[#FFDD00] mb-4">Service Areas</h3>
            <ul className="space-y-3">
              {featuredLocations.slice(0, 8).map((l) => (
                <li key={l.slug}>
                  <Link to={`/locations/${l.slug}`} className="text-sm text-white/70 hover:text-white transition-colors min-h-[32px] flex items-center">
                    Projector Repair {l.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/locations" className="inline-flex items-center gap-2 text-sm text-[#FFDD00] hover:text-white transition-colors min-h-[32px]">
                  View all areas <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-label text-white/70 mb-4">Services</h3>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-white/70 hover:text-[#0057B7] transition-colors min-h-[44px] flex items-center">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="font-label text-white/70 mb-4 mt-8">Company</h3>
            <ul className="space-y-3">
              {companyLinks.slice(0, 3).map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-white/70 hover:text-[#0057B7] transition-colors min-h-[44px] flex items-center">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="container-custom py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30">
            &copy; {new Date().getFullYear()} Wells Electronics Ltd. All rights reserved.
          </p>
          <p className="text-xs text-white/30">Trading as Projector Repair London</p>
        </div>
      </div>
    </footer>
  );
}
